import { useFormContext, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
// Constants
import { TIME_ZONE_OPTIONS, TimeZoneOption } from 'constants/timeZone';
import {
  STREAM_DESCRIPTION_MAX_LENGTH,
  STREAM_NAME_MAX_LENGTH,
  STREAM_TAGS_NOTICE,
} from 'constants/streams';
// Helpers
import {
  formatStreamTag,
  REPEAT_STREAM_OPTIONS,
  RepeatStreamOption,
} from 'helpers/streams';
// Types
import { FormInputs } from 'components/CreatorSetup/StreamingSetup/CreateStreamSetup/CreateStreamSetup';
// Ui
import Input from 'ui3/Input/Input';
import TextArea from 'ui3/TextArea/TextArea';
import TagsInput from 'ui3/TagsInput/TagsInput';
import Switch from 'ui3/Switch/Switch';
import DatePicker from 'ui3/DatePicker/DatePicker';
import Select from 'ui3/Select/Select';
import Text from 'ui3/Text/Text';
import Divider from 'ui3/Divider/Divider';
// Components
import PosterUploader from 'components/common3/PosterUploader/PosterUploader';
// Styles
import styles from './StreamDetailsStep.module.scss';

const StreamDetailsStep = (): JSX.Element => {
  const {
    register,
    errors,
    control,
    watch,
    trigger,
  } = useFormContext<FormInputs>();

  const timeZone = watch('timeZone');
  const scheduleDate = watch('scheduleDate');

  const selectedTimeZoneOffset = dayjs
    .tz(
      dayjs(scheduleDate ? dayjs(scheduleDate) : dayjs()).format(
        'YYYY-MM-DD HH:mm'
      ),
      timeZone?.tzName
    )
    .utcOffset();

  const currentTimeInSelectedTimeZone = dayjs()
    .add(selectedTimeZoneOffset, 'minute')
    .add(new Date().getTimezoneOffset(), 'minute')
    .toDate();

  // minDate only disables past days, the time list stays selectable. minTime /
  // maxTime are no help either: they match on time-of-day alone, so they would
  // cut off future days too.
  const isFutureTimeSlot = (time: Date) =>
    dayjs(time).isAfter(currentTimeInSelectedTimeZone);

  return (
    <div className={styles.root}>
      <div className={styles.columns}>
        <div className={styles.left}>
          <div className={styles.section}>
            <Controller
              control={control}
              name="name"
              render={({ onChange, value }) => (
                <Input
                  name="name"
                  label="Stream Title"
                  placeholder="Add title"
                  value={value || ''}
                  onChange={(e) => onChange(e.target.value)}
                  maxLength={STREAM_NAME_MAX_LENGTH}
                  error={errors?.name?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="description"
              render={({ onChange, value }) => (
                <TextArea
                  name="description"
                  label="Description"
                  placeholder="Add description"
                  value={value || ''}
                  onChange={(e) => onChange(e.target.value)}
                  maxLength={STREAM_DESCRIPTION_MAX_LENGTH}
                  notice="Tell viewers what to expect from your stream."
                  error={errors?.description?.message}
                  rows={4}
                />
              )}
            />
            <Controller
              name="hashtags"
              control={control}
              defaultValue={[]}
              render={({ onChange, value: tags }) => (
                <TagsInput
                  tags={tags || []}
                  label="Tags"
                  placeholder="Add tags here"
                  notice={STREAM_TAGS_NOTICE}
                  formatTag={formatStreamTag}
                  onChange={onChange}
                  name="hashtags"
                />
              )}
            />
          </div>

          <Divider />

          <div className={styles.section}>
            <div className={styles.sectionHeading}>
              <Text variant="subtitle2">Scheduling Details</Text>
              <Text variant="body2Regular" color="lights-medium">
                Set when your stream goes live and where it takes place
              </Text>
            </div>
            <div className={styles.dateTimezoneContainer}>
              <div className={styles.dateTimezoneField}>
                <Controller
                  control={control}
                  name="scheduleDate"
                  render={({ onChange, onBlur, value }) => (
                    <DatePicker
                      showMonthDropdown
                      showYearDropdown
                      yearDropdownItemNumber={70}
                      selected={value ? dayjs(value).toDate() : null}
                      onBlur={onBlur}
                      onChange={(date) => {
                        onChange(date);
                        trigger('scheduleDate');
                      }}
                      showTimeSelect
                      placeholderText="MM/DD/YYYY 00:00AM/PM"
                      minDate={currentTimeInSelectedTimeZone}
                      filterTime={isFutureTimeSlot}
                      customInput={
                        <Input
                          label="Date"
                          aria-label="Select schedule date"
                          name="dateTimeInput"
                          error={errors?.scheduleDate?.message}
                        />
                      }
                    />
                  )}
                />
              </div>
              <div className={styles.dateTimezoneField}>
                <Controller
                  control={control}
                  name="timeZone"
                  render={({ onChange, value }) => {
                    const matchedOption = TIME_ZONE_OPTIONS.find(
                      (option) => option.tzCode === value?.tzCode
                    );

                    return (
                      <Select
                        aria-label="Select Time Zone"
                        placeholder="Time Zone"
                        label="Time zone"
                        options={TIME_ZONE_OPTIONS}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.tzCode}
                        size="medium"
                        id="timeZone"
                        inputId="tzCodeSelect"
                        error={(errors?.timeZone as any)?.message}
                        onChange={(option: TimeZoneOption) => {
                          onChange(option);
                          // A slot that was in the future in the old zone can be
                          // in the past in the new one.
                          trigger('scheduleDate');
                        }}
                        value={matchedOption}
                      />
                    );
                  }}
                />
              </div>
            </div>
            <Controller
              name="repeatsEveryEnum"
              defaultValue={REPEAT_STREAM_OPTIONS[0]}
              control={control}
              render={({ onChange, ...rest }) => (
                <Select
                  placeholder="Select"
                  label="Does this event repeat?"
                  options={REPEAT_STREAM_OPTIONS}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => `${option.value}`}
                  inputId="repeatsEveryEnum"
                  fieldSize="large"
                  onChange={(data: RepeatStreamOption) => onChange(data)}
                  {...rest}
                />
              )}
            />
          </div>

          <Divider />

          <div className={styles.testStreamRow}>
            <div>
              <Text variant="body2Medium">Test Stream</Text>
              <Text variant="body2Regular" color="lights-low">
                Run this as a test stream before going live to your fans.
              </Text>
            </div>
            <Switch name="isHidden" ref={register} />
          </div>
        </div>
        <PosterUploader alt="Stream poster" />
      </div>
    </div>
  );
};

export default StreamDetailsStep;
